<template>
  <div
    class="ptr-indicator"
    :class="{
      'ptr-indicator--visible': pullDistance > 0 || refreshing,
      'ptr-indicator--pulling': pulling,
      'ptr-indicator--ready': readyToRefresh,
      'ptr-indicator--refreshing': refreshing,
    }"
    :style="{ transform: `translate3d(-50%, ${indicatorOffset}px, 0)` }"
    aria-hidden="true"
  >
    <span class="ptr-spinner"></span>
  </div>
</template>

<script>
const MOBILE_QUERY = '(max-width: 768px), (pointer: coarse)';
const ACTIVATION_DISTANCE = 8;
const TRIGGER_DISTANCE = 78;
const MAX_PULL_DISTANCE = 118;
const OVERLAY_SELECTORS = [
  '.mbs-sheet',
  '.mbs-backdrop',
  '.swal2-container.swal2-shown',
  '.swal2-container:not(.swal2-hide)',
  '.p-dialog-mask',
  '.p-dialog',
  '.p-drawer-mask',
  '.p-drawer',
  '.p-sidebar-mask',
  '.p-sidebar',
  '.p-popover',
  '.p-overlaypanel',
  '.p-menu-overlay',
  '.p-select-overlay',
  '.p-datepicker',
  '.modal.show',
  '.offcanvas.show',
  '.dropdown-menu.show',
  '.ep-popup',
  '[aria-modal="true"]',
  '[role="dialog"]',
];

export default {
  name: 'PullToRefresh',
  data() {
    return {
      startX: 0,
      startY: 0,
      pullDistance: 0,
      pulling: false,
      refreshing: false,
      overlayOpen: false,
      mutationObserver: null,
      mediaQuery: null,
    };
  },
  computed: {
    isMobile() {
      return this.mediaQuery?.matches ?? false;
    },
    readyToRefresh() {
      return this.pullDistance >= TRIGGER_DISTANCE;
    },
    indicatorOffset() {
      if (this.refreshing) return 18;
      return Math.min(this.pullDistance * 0.55, 34) - 48;
    },
  },
  mounted() {
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.updateOverlayState();
    this.mutationObserver = new MutationObserver(this.updateOverlayState);
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'aria-modal'],
    });

    window.addEventListener('resize', this.updateOverlayState, { passive: true });
    window.addEventListener('touchstart', this.onTouchStart, { passive: true, capture: true });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true });
    window.addEventListener('touchend', this.onTouchEnd, { passive: true, capture: true });
    window.addEventListener('touchcancel', this.onTouchCancel, { passive: true, capture: true });
  },
  beforeUnmount() {
    this.mutationObserver?.disconnect();
    window.removeEventListener('resize', this.updateOverlayState);
    window.removeEventListener('touchstart', this.onTouchStart, { capture: true });
    window.removeEventListener('touchmove', this.onTouchMove, { capture: true });
    window.removeEventListener('touchend', this.onTouchEnd, { capture: true });
    window.removeEventListener('touchcancel', this.onTouchCancel, { capture: true });
  },
  methods: {
    updateOverlayState() {
      this.overlayOpen = OVERLAY_SELECTORS.some((selector) =>
        Array.from(document.querySelectorAll(selector)).some(this.isVisibleOverlay)
      );
    },
    isVisibleOverlay(element) {
      if (!element || element.closest('.ptr-indicator')) return false;
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity) !== 0 &&
        rect.width > 0 &&
        rect.height > 0
      );
    },
    isAtPageTop() {
      const root = document.scrollingElement || document.documentElement;
      return Math.round(root.scrollTop || window.scrollY || 0) <= 0;
    },
    canStartPull() {
      return (
        this.isMobile &&
        !this.refreshing &&
        !this.overlayOpen &&
        this.isAtPageTop()
      );
    },
    // The PDF viewer scrolls inside its OWN nested container (.pdf-scroll), so the
    // outer page stays at top and a fast vertical swipe inside the PDF would
    // otherwise be misread as a page-pull. When a gesture STARTS inside the PDF,
    // this component stays out of the way entirely (like a native PDF reader).
    isInsidePdf(target) {
      return !!(target && typeof target.closest === 'function' && target.closest('.pdf-scroll'));
    },
    onTouchStart(event) {
      if (event.touches.length !== 1) return;

      this.updateOverlayState();
      const touch = event.touches[0];
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.pullDistance = 0;
      // Never start a pull for a gesture that began inside the PDF scroll area.
      this.pulling = this.canStartPull() && !this.isInsidePdf(event.target);
    },
    onTouchMove(event) {
      if (!this.isMobile || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const deltaX = touch.clientX - this.startX;
      const deltaY = touch.clientY - this.startY;
      const absX = Math.abs(deltaX);
      const verticalPull = deltaY > ACTIVATION_DISTANCE && deltaY > absX * 1.25;

      // While any overlay (bottom sheet, dialog, drawer, ...) is open, this
      // component must stay completely out of the way — no preventDefault,
      // no exceptions. The removed check here used to call preventDefault()
      // based on isAtPageTop(), which reads the OUTER page's scrollTop; but
      // the outer page is frozen (body.style.overflow='hidden') while an
      // overlay is open, so isAtPageTop() is permanently stale/true and had
      // nothing to do with whether the overlay's OWN inner content could
      // still scroll. That mismatch was exactly what created the dead zone:
      // any sufficiently-downward touchmove got preventDefault()'d at the
      // window capture phase — before the overlay's own live-scrollTop-based
      // handler (e.g. navbar.vue's onTouchMove) ever got a chance to decide
      // whether ITS content could still scroll — killing native scrolling
      // for that whole gesture regardless of direction or position. Each
      // overlay already owns its own scroll/drag boundary logic against its
      // own live scroll position; this component must never second-guess it.
      if (this.overlayOpen) {
        this.resetPull();
        return;
      }

      if (!this.pulling) return;
      if (!verticalPull) {
        if (absX > ACTIVATION_DISTANCE && absX > Math.abs(deltaY)) this.resetPull();
        return;
      }

      if (!this.isAtPageTop()) {
        this.resetPull();
        return;
      }

      event.preventDefault();
      this.pullDistance = Math.min((deltaY - ACTIVATION_DISTANCE) * 0.62, MAX_PULL_DISTANCE);
    },
    onTouchEnd() {
      if (!this.pulling) {
        this.resetPull();
        return;
      }

      if (this.readyToRefresh && !this.overlayOpen) {
        this.refreshing = true;
        this.pullDistance = TRIGGER_DISTANCE;
        window.location.reload();
        return;
      }

      this.resetPull();
    },
    onTouchCancel() {
      this.resetPull();
    },
    resetPull() {
      this.pulling = false;
      this.pullDistance = 0;
    },
  },
};
</script>

<style scoped>
.ptr-indicator {
  position: fixed;
  top: 10px;
  left: 50%;
  z-index: 2147483000;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.18);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.18s ease, box-shadow 0.16s ease;
}

.ptr-indicator--visible {
  display: flex;
  opacity: 1;
}

.ptr-indicator--pulling {
  transition: opacity 0.08s ease, box-shadow 0.16s ease;
}

.ptr-spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(91, 95, 239, 0.18);
  border-top-color: #5b5fef;
  border-radius: 50%;
  transform: rotate(0deg);
}

.ptr-indicator--ready {
  box-shadow: 0 12px 34px rgba(91, 95, 239, 0.28);
}

.ptr-indicator--refreshing .ptr-spinner {
  animation: ptr-spin 0.75s linear infinite;
}

@keyframes ptr-spin {
  to { transform: rotate(360deg); }
}

@media (min-width: 769px) and (pointer: fine) {
  .ptr-indicator {
    display: none !important;
  }
}

@media (max-width: 768px), (pointer: coarse) {
  :global(html),
  :global(body) {
    overscroll-behavior-y: contain;
  }
}
</style>
